import React, { useState, useCallback } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Platform,
  ActivityIndicator,
  KeyboardAvoidingView,
  ScrollView,
  StatusBar,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import Icon from 'react-native-vector-icons/Ionicons';
import { strings } from '@/constants/residents';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { REDESIGN_TYPOGRAPHY, REDESIGN_SPACING } from '@/constants/residents/homeConstants';
import { AuthAPI } from '@/services/residents/api/AuthAPI';
import { AuthStackParamList } from '@/navigation/residents/mobile/AuthNavigator';
import { SVG_COMPONENTS } from '@/assets/admin';
import { useToast } from '@/contexts/admin/ToastContext';
import { showAlert } from '@/utils/common/alert';
import { GradientBackground, GlassCard } from '@/components/residents/redesign';

type AccountVerificationRouteProp = RouteProp<AuthStackParamList, 'AccountVerification'>;
type AccountVerificationNavigationProp = StackNavigationProp<AuthStackParamList, 'AccountVerification'>;

export const AccountVerificationScreen: React.FC = () => {
  const { colors } = useThemeColors();
  const navigation = useNavigation<AccountVerificationNavigationProp>();
  const route = useRoute<AccountVerificationRouteProp>();
  const [isResending, setIsResending] = useState(false);
  const { showSuccess, showError } = useToast();

  const email = route.params?.email || '';
  const isSignup = route.params?.isSignup || false;

  const handleResendEmail = useCallback(async () => {
    if (!email) {
      if (Platform.OS === 'web') {
        showError('No email address found.');
      } else {
        showAlert('Error', 'No email address found.');
      }
      return;
    }

    setIsResending(true);
    try {
      const response = await AuthAPI.sendVerification(email);

      if (response.success) {
        if (Platform.OS === 'web') {
          showSuccess('Verification email successfully sent');
        } else {
          showAlert('Email Sent', 'Verification email successfully sent');
        }
      } else {
        const errorMsg = response.message || 'Failed to resend verification email.';
        if (Platform.OS === 'web') {
          showError(errorMsg);
        } else {
          showAlert('Error', errorMsg);
        }
      }
    } catch (error: any) {
      const errorMsg = error.message || 'An error occurred while resending the verification email.';
      if (Platform.OS === 'web') {
        showError(errorMsg);
      } else {
        showAlert('Error', errorMsg);
      }
    } finally {
      setIsResending(false);
    }
  }, [email, showSuccess, showError]);

  const handleGoBack = useCallback(() => {
    if (navigation.canGoBack()) {
      navigation.goBack();
      return;
    }
    if (!isSignup) {
      navigation.navigate('Login');
    }
  }, [navigation, isSignup]);

  return (
    <GradientBackground>
      <StatusBar barStyle="light-content" />
      <SafeAreaView style={styles.container} edges={['top', 'bottom']}>
        <KeyboardAvoidingView
          style={styles.keyboardContainer}
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        >
          <View style={styles.navigationBar}>
            <TouchableOpacity
              style={[styles.backButton, { backgroundColor: colors.glass.buttonBg, borderColor: colors.glass.buttonBorder }]}
              onPress={handleGoBack}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            >
              <Icon name="chevron-back" size={22} color={colors.text.primary} />
            </TouchableOpacity>
          </View>

          <ScrollView contentContainerStyle={styles.scrollContainer}>
            <View style={styles.contentContainer}>
              <GlassCard borderRadius={20} padding={32}>
                <View style={styles.cardContent}>
                  <View style={styles.iconContainer}>
                    <SVG_COMPONENTS.SEND_INVITATION
                      width={64}
                      height={64}
                      color="#FFFFFF"
                      style={styles.mailIcon}
                    />
                  </View>

                  <Text style={[styles.titleLabel, { color: colors.text.primary }]}>{strings.verification.accountVerifyTitle}</Text>

                  <Text style={[styles.subtitleLabel, { color: colors.text.muted }]}>
                    {strings.verification.accountVerifyDescription}
                  </Text>

                  <View style={styles.buttonContainer}>
                    <TouchableOpacity
                      style={[styles.button, styles.resendButton, { backgroundColor: colors.glass.buttonBg }]}
                      onPress={handleResendEmail}
                      disabled={isResending}
                    >
                      {isResending ? (
                        <ActivityIndicator color={PRIMITIVE_COLORS.resioBlue} size="small" />
                      ) : (
                        <Text style={styles.resendButtonText}>{strings.verification.accountVerifyResend}</Text>
                      )}
                    </TouchableOpacity>

                    <TouchableOpacity
                      style={[styles.button, styles.goBackButton]}
                      onPress={handleGoBack}
                      disabled={isResending}
                    >
                      <Text style={styles.goBackButtonText}>{strings.verification.accountVerifyCancel}</Text>
                    </TouchableOpacity>
                  </View>
                </View>
              </GlassCard>
            </View>
          </ScrollView>
        </KeyboardAvoidingView>
      </SafeAreaView>
    </GradientBackground>
  );
};
const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  keyboardContainer: {
    flex: 1,
  },
  navigationBar: {
    height: 44,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
  },
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 10,
    borderWidth: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  scrollContainer: {
    flexGrow: 1,
    justifyContent: 'center',
  },
  contentContainer: {
    paddingHorizontal: REDESIGN_SPACING.horizontalPadding,
  },
  cardContent: {
    alignItems: 'center',
    gap: 16,
  },
  iconContainer: {
    marginBottom: 8,
    alignItems: 'center',
  },
  titleLabel: {
    ...REDESIGN_TYPOGRAPHY.heading1,
    fontSize: 22,
    textAlign: 'center',
  },
  subtitleLabel: {
    ...REDESIGN_TYPOGRAPHY.body,
    textAlign: 'center',
    lineHeight: 20,
  },
  mailIcon: {
    opacity: 0.8,
  },
  buttonContainer: {
    width: '100%',
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 12,
    marginTop: 16,
  },
  button: {
    flex: 1,
    borderRadius: REDESIGN_SPACING.buttonRadius,
    paddingVertical: 12,
    paddingHorizontal: 16,
    height: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  resendButton: {
    borderWidth: 1,
    borderColor: PRIMITIVE_COLORS.resioBlue,
  },
  resendButtonText: {
    color: PRIMITIVE_COLORS.resioBlue,
    fontSize: 13,
    fontWeight: '600',
  },
  goBackButton: {
    backgroundColor: PRIMITIVE_COLORS.resioBlue,
  },
  goBackButtonText: {
    color: '#FFFFFF',
    fontSize: 13,
    fontWeight: '600',
  },
});
